import { Injectable } from '@angular/core';
import { ProfileStorageService } from './profile-storage.service';
import { Profiel, ProfielFunctie, ProfielOpleiding } from '../../model/profiel/profiel';
import { ProfielResource } from '../resources/profiel.resource';
import { StateService } from '../state/state.service';
import { Observable, Observer } from 'rxjs';
import { Functie } from '../../model';
import { Opleiding } from '../../model/opleiding/opleiding';
import { StorageService } from '../storage/storage.service';
import { PersonalSelectionModal } from '../../components/profile/match/selector/personal-selection-selector.model';

@Injectable({
  providedIn: 'root'
})
export class ProfielService {
  public constructor(
    private profileStorageService: ProfileStorageService,
    private profielResource: ProfielResource,
    private stateService: StateService,
    private storageService: StorageService
  ) {
  }

  public updateProfielWithChatterResult(chatterProfielEvent: any): Observable<any> {
    return new Observable((observer: Observer<boolean>) => {
      this.profielResource.getProfiel().subscribe((result: any) => {
        const existingProfiel: Profiel = this.createExistingProfiel(result);
        const chatterProfiel = this.convertWrapperToProfiel(chatterProfielEvent);

        const mergedInteresses = existingProfiel.interesses.concat(
          chatterProfiel.interesses.filter(
            interesse => !existingProfiel.interesses.find(existingInt => existingInt.esId === interesse.esId)
          )
        );

        const updatedProfiel = {
          ...existingProfiel,
          functies: existingProfiel.functies.concat(chatterProfiel.functies),
          opleidingen: existingProfiel.opleidingen.concat(chatterProfiel.opleidingen),
          interesses: mergedInteresses
        };

        this.profielResource.updateProfiel(updatedProfiel).subscribe((resultProfiel: Profiel) => {
          this.profileStorageService.saveProfileInStorage(resultProfiel);
          const personalProfileMatch: PersonalSelectionModal = PersonalSelectionModal.fromProfile(updatedProfiel);

          personalProfileMatch.functies.forEach(x => x[1] = false);
          personalProfileMatch.opleidingen.forEach(x => x[1] = false);
          personalProfileMatch.interessesDoelgroep.forEach(x => x[1] = false);
          personalProfileMatch.interessesSoortwerk.forEach(x => x[1] = false);
          personalProfileMatch.interessesWerkomstandigheden.forEach(x => x[1] = false);

          this.personalProfileMatchSelector(personalProfileMatch.functies, chatterProfiel.functies);
          this.personalProfileMatchSelector(personalProfileMatch.opleidingen, chatterProfiel.opleidingen);
          this.personalProfileMatchSelector(personalProfileMatch.interessesDoelgroep, chatterProfiel.interesses);
          this.personalProfileMatchSelector(personalProfileMatch.interessesSoortwerk, chatterProfiel.interesses);
          this.personalProfileMatchSelector(personalProfileMatch.interessesWerkomstandigheden, chatterProfiel.interesses);

          this.storageService.set('profile-match', personalProfileMatch);

          observer.next(true);
          observer.complete();
        });
      });
    });
  }

  private personalProfileMatchSelector(personalProfileMatchItem, chatterProfielItem): void {
    personalProfileMatchItem.forEach(profileMatchItem => {
      profileMatchItem[1] = chatterProfielItem.some(x => x.esId === profileMatchItem[0].esId);
    });
  }

  private createExistingProfiel(uncheckedProfiel: any): Profiel {
    return <Profiel>{
      functies: uncheckedProfiel.functies || [],
      opleidingen: uncheckedProfiel.opleidingen || [],
      interesses: uncheckedProfiel.interesses || [],
      naam: uncheckedProfiel.naam,
      achternaam: uncheckedProfiel.achternaam,
      geslacht: uncheckedProfiel.geslacht,
      geboortedatum: uncheckedProfiel.geboortedatum,
      straat: uncheckedProfiel.straat,
      straatNummer: uncheckedProfiel.straatNummer,
      postcode: uncheckedProfiel.postcode,
      woonplaats: uncheckedProfiel.woonplaats,
      telefoonnummer: uncheckedProfiel.telefoonnummer,
      uploads: uncheckedProfiel.uploads,
      correlationId: uncheckedProfiel.correlationId
    };
  }

  private convertWrapperToProfiel(profileWrapper: any): Profiel {
    const profiel = <Profiel>{
      functies: [],
      opleidingen: [],
      interesses: []
    };

    const responseMap: any = profileWrapper.responseMap;
    let voorkeuren: any = [];

    if (responseMap.$functie?.data?.length > 0) {
      profiel.functies = profiel.functies.concat(
        responseMap.$functie.data.map(functie => this.mapChatterEntityToProfielEntity(functie))
      );
    }

    if (responseMap.$zijInstroomFunctie?.data?.length > 0) {
      profiel.functies = profiel.functies.concat(
        responseMap.$zijInstroomFunctie.data.map(functie => this.mapChatterEntityToProfielEntity(functie))
      );
    }

    if (responseMap.$zijInstroomFunctieRetry?.data?.length > 0) {
      profiel.functies = profiel.functies.concat(
        responseMap.$zijInstroomFunctieRetry.data.map(functie => this.mapChatterEntityToProfielEntity(functie))
      );
    }

    if (responseMap.$functieRetry?.data?.length > 0) {
      profiel.functies = profiel.functies.concat(
        responseMap.$functieRetry.data.map(functie => this.mapChatterEntityToProfielEntity(functie))
      );
    }

    if (responseMap.$opleiding?.data?.length > 0) {
      profiel.opleidingen = profiel.opleidingen.concat(
        responseMap.$opleiding.data.map(opleiding => this.mapChatterEntityToProfielEntity(opleiding))
      );
    }
    if (responseMap.$opleidingRetry?.data?.length > 0) {
      profiel.opleidingen = profiel.opleidingen.concat(
        responseMap.$opleidingRetry.data.map(opleiding => this.mapChatterEntityToProfielEntity(opleiding))
      );
    }

    if (responseMap.$voorkeurenDoelgroep?.data) {
      const doelgroepVoorkeuren = responseMap.$voorkeurenDoelgroep.data.map(item => ({ ...item, soort: 'doelgroep' }));
      voorkeuren = voorkeuren.concat(doelgroepVoorkeuren);
    }
    if (responseMap.$voorkeurenSoortWerk?.data) {
      const soortWerkVoorkeuren = responseMap.$voorkeurenSoortWerk.data.map(item => ({ ...item, soort: 'soort werk' }));
      voorkeuren = voorkeuren.concat(soortWerkVoorkeuren);
    }
    if (responseMap.$voorkeurenWerkomstandigheden?.data) {
      const doelgroepVoorkeuren = responseMap.$voorkeurenWerkomstandigheden.data.map(item => ({
        ...item,
        soort: 'werkomstandigheden'
      }));
      voorkeuren = voorkeuren.concat(doelgroepVoorkeuren);
    }

    voorkeuren
      .filter(item => item.selected)
      .forEach(item =>
        profiel.interesses.push({
          esId: +item.id,
          naam: item.name,
          soort: item.soort,
          uitsluiten: false
        })
      );

    return profiel;
  }

  private mapChatterEntityToProfielEntity(entity: Functie | Opleiding): ProfielFunctie | ProfielOpleiding {
    return {
      ...entity,
      esId: entity.id,
      id: null
    };
  }
}
